import React, { CSSProperties, useEffect, useState } from 'react';

const DEFAULT_FALLBACK_IMAGE = '/projects/lumbra/portada.png';

interface ProjectMiniatureMediaProps {
  src: string;
  alt: string;
  title: string;
  subtitle: string;
  fallbackSrc?: string;
  wrapperClassName?: string;
  imageClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  titleStyle?: CSSProperties;
  subtitleStyle?: CSSProperties;
  imageScale?: number;
  subtitleFirst?: boolean;
  titleAs?: 'h3' | 'h4';
  children?: React.ReactNode;
}

const ProjectMiniatureMedia: React.FC<ProjectMiniatureMediaProps> = ({
  src,
  alt,
  title,
  subtitle,
  fallbackSrc,
  wrapperClassName = 'relative overflow-hidden aspect-[4/3]',
  imageClassName = 'w-full h-full object-cover transition-transform duration-500',
  contentClassName = 'absolute bottom-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent',
  titleClassName = 'font-pixel font-bold mb-1.5 sm:mb-2 text-lg sm:text-xl md:text-2xl',
  subtitleClassName = 'text-xs sm:text-sm',
  titleStyle,
  subtitleStyle,
  imageScale = 1,
  subtitleFirst = false,
  titleAs = 'h3',
  children,
}) => {
  const [resolvedSrc, setResolvedSrc] = useState(src);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setResolvedSrc(src);
    setLoaded(false);
  }, [src]);

  const onImageError = () => {
    setLoaded(false);
    if (fallbackSrc && resolvedSrc !== fallbackSrc) {
      setResolvedSrc(fallbackSrc);
      return;
    }
    if (resolvedSrc !== DEFAULT_FALLBACK_IMAGE) {
      setResolvedSrc(DEFAULT_FALLBACK_IMAGE);
    }
  };

  const TitleTag = titleAs;

  return (
    <div className={wrapperClassName}>
      <img
        src={resolvedSrc}
        alt={alt}
        className={imageClassName}
        style={{
          transform: `scale(${imageScale})`,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease, transform 0.5s ease',
        }}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={onImageError}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />

      <div className={contentClassName}>
        {subtitleFirst ? (
          <>
            <p className={subtitleClassName} style={subtitleStyle}>
              {subtitle}
            </p>
            <TitleTag className={titleClassName} style={titleStyle}>
              {title}
            </TitleTag>
          </>
        ) : (
          <>
            <TitleTag className={titleClassName} style={titleStyle}>
              {title}
            </TitleTag>
            <p className={subtitleClassName} style={subtitleStyle}>
              {subtitle}
            </p>
          </>
        )}
        {children}
      </div>
    </div>
  );
};

export default ProjectMiniatureMedia;
